import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, money, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  Building2,
  DollarSign,
  Package,
  Trophy,
  Eye,
  Gavel,
  Plus,
  Zap,
  Sparkles,
  ExternalLink,
  Trash2,
  Save,
  Wallet,
  CheckCircle2,
  Ticket,
} from "lucide-react";

const inputCls = "rounded-none bg-zinc-950 border-zinc-800 focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-0 industrial-input h-11";

export default function SellerDashboard() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState({
    business_name: "",
    business_phone: "",
    business_city: "",
    business_state: "",
    business_license: "",
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [listings, setListings] = useState([]);
  const [boostingId, setBoostingId] = useState(null);
  const [promoInput, setPromoInput] = useState("");
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [creditHistory, setCreditHistory] = useState([]);

  const load = async () => {
    try {
      const [p, s, l, h] = await Promise.all([
        api.get("/sellers/profile"),
        api.get("/sellers/stats"),
        api.get("/auctions/mine/selling"),
        api.get("/credit/history"),
      ]);
      setProfile({
        business_name: p.data.business_name || "",
        business_phone: p.data.business_phone || "",
        business_city: p.data.business_city || "",
        business_state: p.data.business_state || "",
        business_license: p.data.business_license || "",
      });
      setStats(s.data);
      setListings(l.data);
      setCreditHistory(h.data);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const redeemCredit = async () => {
    setRedeeming(true);
    try {
      const { data } = await api.post("/credit/request-payout", {});
      toast.success(`Payout requested: $${data.amount}. You'll receive it via ACH or check within 3-5 business days.`);
      load();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setRedeeming(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.put("/sellers/profile", profile);
      await refresh();
      toast.success("Business profile saved");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSavingProfile(false);
    }
  };

  const boostListing = async (auctionId) => {
    setBoostingId(auctionId);
    try {
      const { data } = await api.post("/boost/checkout", {
        auction_id: auctionId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatError(e));
      setBoostingId(null);
    }
  };

  const deleteListing = async (auctionId) => {
    if (!window.confirm("Delete this listing? This cannot be undone.")) return;
    try {
      await api.delete(`/auctions/${auctionId}`);
      toast.success("Listing deleted");
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const applyPromo = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    setApplyingPromo(true);
    try {
      const { data } = await api.post("/sellers/apply-promo", { code });
      toast.success(`Promo ${data.code} applied! ${data.description}`);
      setPromoInput("");
      load();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setApplyingPromo(false);
    }
  };

  const setP = (k) => (e) => setProfile((p) => ({ ...p, [k]: e.target.value }));

  if (!user || typeof user !== "object") {
    return <div className="max-w-7xl mx-auto p-8 text-zinc-500 uppercase tracking-widest">Loading…</div>;
  }

  const businessName = profile.business_name || user.name;
  const isFirstTime = !stats || (stats.total_listings === 0 && !profile.business_name);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Building2 className="w-4 h-4 text-orange-500" />
            <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500">Seller Command Center</div>
          </div>
          <h1 data-testid="seller-heading" className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter">
            {businessName}
          </h1>
          <p className="text-sm text-zinc-500 mt-2">
            Your control room — post units, track bids, cash out.
          </p>
        </div>
        <Button
          asChild
          data-testid="new-auction-button"
          className="rounded-none bg-orange-600 hover:bg-orange-500 border border-orange-500 h-12 px-8 text-sm uppercase tracking-widest font-semibold self-start md:self-end"
        >
          <Link to="/sell">
            <Plus className="w-4 h-4 mr-2" /> Create New Auction
          </Link>
        </Button>
      </div>

      {/* First-time nudge */}
      {isFirstTime && (
        <div className="mb-8 bg-orange-500/10 border border-orange-500/40 p-6 flex items-start gap-4">
          <div className="w-10 h-10 bg-orange-500 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-display text-xl font-bold uppercase tracking-tight">Welcome — let&apos;s get you selling.</div>
            <div className="text-sm text-zinc-400 mt-1">
              Complete your business profile below, then hit <b className="text-white">Create New Auction</b> to list your first unit.
              You can upload photos, set your terms, and start collecting bids in under 5 minutes.
            </div>
          </div>
        </div>
      )}

      {/* Promo code banner */}
      {stats && !stats.promo_code && (
        <div data-testid="promo-banner" className="mb-8 bg-zinc-950 border border-orange-500/40 p-6 flex flex-col md:flex-row md:items-center gap-4 justify-between">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-orange-500 flex items-center justify-center shrink-0">
              <Ticket className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-display text-lg font-bold uppercase tracking-tight text-white">Got a promo code?</div>
              <div className="text-sm text-zinc-400 mt-0.5">
                New sellers can enter <b className="text-orange-500 font-mono">VAULT25</b> to earn a <b className="text-white">$25 credit</b> on each of your first 3 listings.
              </div>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Input
              data-testid="promo-input"
              value={promoInput}
              onChange={(e) => setPromoInput(e.target.value)}
              placeholder="VAULT25"
              maxLength={32}
              className="rounded-none bg-black border-zinc-800 focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-0 industrial-input h-11 w-40 font-mono uppercase tracking-wider"
            />
            <Button
              onClick={applyPromo}
              disabled={applyingPromo || !promoInput.trim()}
              data-testid="promo-apply"
              className="rounded-none bg-orange-600 hover:bg-orange-500 border border-orange-500 h-11 px-6 text-xs uppercase tracking-widest font-semibold"
            >
              {applyingPromo ? "Applying…" : "Apply"}
            </Button>
          </div>
        </div>
      )}

      {/* Active promo status */}
      {stats && stats.promo_code && stats.promo_credits_earned < 3 && (
        <div data-testid="promo-active" className="mb-8 bg-emerald-500/5 border border-emerald-500/40 p-4 flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
          <div className="text-sm text-zinc-300">
            <b className="text-emerald-500 font-mono">{stats.promo_code}</b> active — <b className="text-white">{3 - stats.promo_credits_earned} of 3</b> bonus listings remaining. Next new listing = <b className="text-orange-500">+$25 credit</b>.
          </div>
        </div>
      )}

      {/* Credit redemption CTA */}
      {stats && stats.credit_balance > 0 && (
        <div data-testid="credit-redemption-card" className="mb-8 bg-orange-500/5 border border-orange-500 p-6 flex flex-col md:flex-row md:items-center gap-4 justify-between">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-orange-500 flex items-center justify-center shrink-0">
              <DollarSign className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-display text-lg font-bold uppercase tracking-tight text-white">
                Cash credit ready — {money(stats.credit_balance)}
              </div>
              <div className="text-sm text-zinc-400 mt-0.5">
                Request payout and StorageTreasureVault will send <b className="text-white">{money(stats.credit_balance)}</b> to you via ACH or check within 3–5 business days.
              </div>
            </div>
          </div>
          <Button
            onClick={redeemCredit}
            disabled={redeeming}
            data-testid="redeem-credit"
            className="rounded-none bg-orange-600 hover:bg-orange-500 border border-orange-500 h-11 px-6 text-xs uppercase tracking-widest font-semibold shrink-0"
          >
            {redeeming ? "Requesting…" : `Request ${money(stats.credit_balance)} Payout`}
          </Button>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          <StatCard icon={<Package />} label="Active Listings" value={stats.active_listings} testid="stat-active" />
          <StatCard icon={<Trophy />} label="Sold" value={stats.sold} testid="stat-sold" />
          <StatCard icon={<Gavel />} label="Bids Received" value={stats.total_bids_received} testid="stat-bids" />
          <StatCard icon={<Eye />} label="Total Views" value={stats.total_views} testid="stat-views" />
          <StatCard icon={<DollarSign />} label="Paid Out" value={money(stats.paid_out)} accent testid="stat-paid-out" />
          <StatCard icon={<Wallet />} label="Pending Payout" value={money(stats.pending_payout)} testid="stat-pending" />
          <StatCard icon={<Trophy />} label="Awaiting Buyer" value={money(stats.awaiting_buyer)} testid="stat-awaiting" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        {/* Listings table */}
        <section className="bg-zinc-950 border border-zinc-800">
          <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
            <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500">My Listings</div>
            <div className="text-xs text-zinc-500">{listings.length} total</div>
          </div>
          {listings.length === 0 ? (
            <div className="p-12 text-center">
              <Package className="w-10 h-10 text-zinc-700 mx-auto" />
              <div className="mt-4 font-display text-xl uppercase tracking-tight text-zinc-500">No listings yet</div>
              <Button asChild variant="ghost" className="mt-4 rounded-none text-orange-500 hover:bg-zinc-900">
                <Link to="/sell">Create your first auction →</Link>
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Unit</TableHead>
                    <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 text-right">Current Bid</TableHead>
                    <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 text-right">Bids</TableHead>
                    <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Status</TableHead>
                    <TableHead className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {listings.map((a) => (
                    <TableRow key={a.id} data-testid={`listing-row-${a.id}`} className="border-zinc-900 hover:bg-zinc-900/50">
                      <TableCell className="max-w-xs">
                        <div className="flex items-center gap-3">
                          <img src={a.hero_image || a.photos?.[0] || "https://images.unsplash.com/photo-1649313444539-a8900c5cdc54?w=100&auto=format&fit=crop&q=80"} alt="" className="w-10 h-10 object-cover border border-zinc-800" />
                          <div className="min-w-0">
                            <div className="font-semibold text-white truncate flex items-center gap-1.5">
                              {a.title}
                              {a.is_featured && <Sparkles className="w-3 h-3 text-orange-500" />}
                            </div>
                            <div className="text-xs text-zinc-500 truncate">{a.city}, {a.state} · {a.unit_size}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-display font-bold tabular-nums text-orange-500">{money(a.current_bid || a.starting_bid)}</TableCell>
                      <TableCell className="text-right tabular-nums text-zinc-300">{a.bid_count}</TableCell>
                      <TableCell><StatusBadge status={a.status} paid={a.payment_status === "paid"} /></TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-1">
                          <Button asChild variant="ghost" size="sm" className="rounded-none h-8 px-2 text-zinc-400 hover:text-white hover:bg-zinc-900">
                            <Link to={`/auctions/${a.id}`} data-testid={`view-listing-${a.id}`}><ExternalLink className="w-3.5 h-3.5" /></Link>
                          </Button>
                          {a.status === "active" && !a.is_featured && (
                            <Button
                              onClick={() => boostListing(a.id)}
                              disabled={boostingId === a.id}
                              data-testid={`boost-listing-${a.id}`}
                              variant="ghost"
                              size="sm"
                              className="rounded-none h-8 px-2 text-orange-500 hover:text-orange-400 hover:bg-orange-500/10"
                              title="Feature this listing for $15/7 days"
                            >
                              <Zap className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          <Button
                            onClick={() => deleteListing(a.id)}
                            data-testid={`delete-listing-${a.id}`}
                            variant="ghost"
                            size="sm"
                            className="rounded-none h-8 px-2 text-zinc-400 hover:text-red-500 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </section>

        {/* Business profile */}
        <aside>
          <form onSubmit={saveProfile} className="bg-zinc-950 border border-zinc-800 p-6 space-y-4">
            <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500">Business Profile</div>
            <div>
              <Label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Business Name</Label>
              <Input data-testid="profile-business-name" required value={profile.business_name} onChange={setP("business_name")} className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">City</Label>
                <Input data-testid="profile-business-city" value={profile.business_city} onChange={setP("business_city")} className={inputCls} />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">State</Label>
                <Input data-testid="profile-business-state" maxLength={2} value={profile.business_state} onChange={setP("business_state")} className={inputCls + " uppercase"} />
              </div>
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Phone</Label>
              <Input data-testid="profile-business-phone" type="tel" value={profile.business_phone} onChange={setP("business_phone")} className={inputCls} />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">License / Lien-Holder ID</Label>
              <Input data-testid="profile-business-license" value={profile.business_license} onChange={setP("business_license")} className={inputCls} />
            </div>
            <Button
              type="submit"
              data-testid="save-profile"
              disabled={savingProfile}
              className="w-full rounded-none bg-orange-600 hover:bg-orange-500 border border-orange-500 h-11 text-xs uppercase tracking-widest font-semibold"
            >
              <Save className="w-4 h-4 mr-2" /> {savingProfile ? "Saving…" : "Save Profile"}
            </Button>
          </form>

          <div className="mt-4 bg-zinc-950 border border-zinc-800 p-6">
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3">Payout status</div>
            <div className="flex items-center gap-2 text-sm text-emerald-500">
              <CheckCircle2 className="w-4 h-4" />
              <span>Manual payouts via admin</span>
            </div>
            <div className="text-xs text-zinc-500 mt-2 leading-relaxed">
              You receive 100% of the winning bid on every sale. StorageVault&apos;s only fee is the 8% buyer&apos;s premium charged to the buyer at checkout. Payouts are sent via ACH or check within 3–5 business days after the buyer completes payment.
            </div>
          </div>

          {/* Credit redemption history */}
          {creditHistory && creditHistory.length > 0 && (
            <div className="mt-4 bg-zinc-950 border border-zinc-800 p-6" data-testid="credit-history">
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3">Credit redemption history</div>
              <ul className="space-y-2">
                {creditHistory.slice(0, 5).map((r) => (
                  <li key={r.id} className="flex items-center justify-between text-xs py-2 border-b border-zinc-900 last:border-0">
                    <div>
                      <div className="font-display font-bold tabular-nums text-white">{money(r.amount)}</div>
                      <div className="text-zinc-500">{new Date(r.requested_at).toLocaleDateString()}</div>
                    </div>
                    <span className={`inline-block px-2 py-0.5 text-[9px] font-semibold tracking-widest uppercase border ${
                      r.status === "paid"
                        ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/40"
                        : "bg-orange-500/10 text-orange-500 border-orange-500/40"
                    }`}>
                      {r.status === "paid" ? "Paid by StorageTreasureVault" : "Pending payout"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, accent, testid }) {
  return (
    <div data-testid={testid} className={`bg-zinc-950 border p-4 ${accent ? "border-orange-500" : "border-zinc-800"}`}>
      <div className={accent ? "text-orange-500" : "text-zinc-500"}>{icon}</div>
      <div className={`font-display text-2xl font-black mt-3 tabular-nums ${accent ? "text-orange-500" : "text-white"}`}>{value}</div>
      <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-1">{label}</div>
    </div>
  );
}

function StatusBadge({ status, paid }) {
  if (status === "sold" && paid) {
    return <span className="inline-block px-2 py-0.5 text-[10px] font-semibold tracking-widest uppercase border bg-emerald-500/10 text-emerald-500 border-emerald-500/40">Paid</span>;
  }
  const map = {
    active: "bg-orange-500/10 text-orange-500 border-orange-500/40",
    sold: "bg-emerald-500/10 text-emerald-500 border-emerald-500/40",
    ended: "bg-zinc-500/10 text-zinc-500 border-zinc-700",
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold tracking-widest uppercase border ${map[status] || map.ended}`}>
      {status}
    </span>
  );
}
